import { CheckCircleFilled, MailOutlined, TeamOutlined } from "@ant-design/icons";
import { Button, Divider, Form, Input, Space, Tag, Typography, message } from "antd";
import { useState } from "react";
import {
  type ProviderInviteState,
  type SetupPortal,
  type SetupProvider,
  useCompleteGoLiveMutation,
  useInviteProviderMutation,
} from "../../service/implementation.service";
import styles from "./setup.module.scss";
import { apiErrorMessage } from "./setup.shared";

const { Text } = Typography;

/**
 * Step 6 — invite clinicians and go live.
 *
 * When we've pulled the practice's provider directory from their EHR, each
 * clinician is a row with a one-click invite. eCW's directory carries names, not
 * email addresses, so a row without one shows an inline email field to fill.
 * Below that, a free-form invite for anyone not in the directory, and a "we're
 * live" button that completes the step (only the customer knows when everyone's
 * in). Member Management stays linked for ongoing management.
 */
export default function GoLiveStep({ token, portal }: { token: string; portal: SetupPortal }) {
  const goLive = portal.go_live;
  const [invite] = useInviteProviderMutation();
  const [complete, { isLoading: completing }] = useCompleteGoLiveMutation();
  // Per-row email drafts for providers the directory gave us no address for.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  // Per-row busy tracked locally, NOT off the shared mutation hook: one hook's
  // isLoading would light every row at once, and it can linger past a refetch.
  const [pendingRow, setPendingRow] = useState<string | null>(null);
  // eCW's directory carries no emails, so the server can't match an invite back
  // to a nameless-email row on reload. Track locally-invited rows so the row
  // reflects it immediately after the customer acts.
  const [localInvited, setLocalInvited] = useState<Record<string, ProviderInviteState>>({});

  const isComplete = portal.steps.find((s) => s.key === "go_live")?.status === "complete";

  const rowKey = (p: SetupProvider, i: number) => p.ehr_provider_id || p.username || `${p.name}-${i}`;

  const doInvite = async (email: string, name: string, key: string) => {
    const clean = email.trim();
    if (!clean) {
      message.warning("Enter an email address to invite them.");
      return;
    }
    setPendingRow(key);
    try {
      const res = await invite({ token, email: clean, name }).unwrap();
      const state = res.invite_result?.state ?? "invited";
      if (key !== "__free__") setLocalInvited((m) => ({ ...m, [key]: state }));
      message.success(state === "joined" ? `${clean} was added to your group.` : `Invitation sent to ${clean}.`);
    } catch (err) {
      message.error(apiErrorMessage(err, "We couldn't send that invitation. Please try again."));
    } finally {
      setPendingRow((cur) => (cur === key ? null : cur));
    }
  };

  const inviteTag = (state: SetupProvider["invite_state"]) => {
    if (state === "joined") {
      return (
        <Tag color="green" bordered={false}>
          <CheckCircleFilled /> On Nudge
        </Tag>
      );
    }
    if (state === "invited") {
      return (
        <Tag color="blue" bordered={false}>
          Invited
        </Tag>
      );
    }
    return null;
  };

  return (
    <>
      {goLive.has_directory && goLive.providers.length > 0 ? (
        <>
          <p className={styles.sectionLabel} style={{ marginTop: 4 }}>
            Clinicians in your EHR
          </p>
          <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 12 }}>
            Pulled from your EHR. Invite everyone who should write notes in Nudge — each one gets a link to set up their
            own login.
          </Text>

          <div className={styles.providerList}>
            {goLive.providers.map((p, i) => {
              const key = rowKey(p, i);
              const draft = drafts[key] ?? p.email ?? "";
              const state = localInvited[key] ?? p.invite_state;
              const done = state === "joined" || state === "invited";
              return (
                <div key={key} className={styles.providerRow}>
                  <div className={styles.providerMeta}>
                    <span className={styles.providerName}>
                      {p.name}
                      {p.credentials ? <span className={styles.providerCred}>, {p.credentials}</span> : null}
                    </span>
                    {p.specialty ? (
                      <Text type="secondary" className={styles.providerSpecialty}>
                        {p.specialty}
                      </Text>
                    ) : null}
                  </div>
                  <div className={styles.providerAction}>
                    {done ? (
                      inviteTag(state)
                    ) : (
                      <Space.Compact style={{ width: "100%", maxWidth: 320 }}>
                        <Input
                          placeholder="their@email.com"
                          type="email"
                          value={draft}
                          onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                          onPressEnter={() => doInvite(draft, p.name, key)}
                        />
                        <Button
                          type="primary"
                          loading={pendingRow === key}
                          onClick={() => doInvite(draft, p.name, key)}
                        >
                          Invite
                        </Button>
                      </Space.Compact>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <Divider style={{ margin: "18px 0 14px" }} />
        </>
      ) : (
        <Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 14 }}>
          We'll pull your clinician list from your EHR once your login is verified — you'll be able to invite everyone
          from here. In the meantime you can invite anyone by email below.
        </Text>
      )}

      <p className={styles.sectionLabel}>Invite someone by email</p>
      <Form
        layout="inline"
        onFinish={(v: { email: string }) => doInvite(v.email, "", "__free__")}
        style={{ marginBottom: 8, rowGap: 8 }}
      >
        <Form.Item
          name="email"
          rules={[
            { required: true, message: "Enter an email address." },
            { type: "email", message: "That doesn't look like an email address." },
          ]}
          style={{ flex: 1, minWidth: 220 }}
        >
          <Input prefix={<MailOutlined />} placeholder="colleague@yourpractice.com" />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={pendingRow === "__free__"}>
            Send invite
          </Button>
        </Form.Item>
      </Form>

      <Divider style={{ margin: "14px 0" }} />

      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <Button
          icon={<TeamOutlined />}
          href={goLive.member_management_url || portal.support.member_management_url || "/members"}
          target="_blank"
          rel="noopener noreferrer"
        >
          Manage members in Member Management
        </Button>
        {!isComplete ? (
          <Button
            type="primary"
            loading={completing}
            onClick={async () => {
              try {
                await complete({ token }).unwrap();
                message.success("You're all set — welcome aboard!");
              } catch (err) {
                message.error(apiErrorMessage(err, "We couldn't update that. Please try again."));
              }
            }}
          >
            We've invited everyone — we're live
          </Button>
        ) : null}
      </Space>
    </>
  );
}
